// A pinned scene that moves through numbered steps as the page scrolls.
//
//   const track = useRef(null);
//   const sticky = useRef(null);
//   const { active, pinned, goTo } = usePinnedSteps(track, {
//       stickyRef: sticky,
//       count: 5,        // steps
//       stepPx: 400,     // omit to divide the track's run evenly (Pillars);
//                        // pass a fixed distance per step (Method)
//   });
//
// `track` is the tall element and `sticky` the position:sticky child; `sticky`
// reads its own CSS `top`, so a stage whose top is not 0 works unchanged.
//
//   active   the current step. React state that changes only at a boundary.
//   pinned   false below 1024 or with reduced motion. Use it to choose the
//            stacked layout; `active` stays 0 and nothing is measured.
//   goTo(i)  scrolls just inside step i, with the page's easing.
//
// It writes --ld-pin (0 → count, unsmoothed, every frame) on the track, for
// anything that follows the scrollbar exactly, like the Pillars rail:
//   transform: scaleY(clamp(0, calc(var(--ld-pin, 0) - var(--i)), 1));
//
// Pages/Home/motion/usePinProgress assumes the pinned child is exactly one
// screen tall and stuck at 0, and reports 0 → 1 with no steps, so it does not fit.

import { useCallback, useEffect, useRef, useState } from "react";
import { clamp01, DESKTOP, getScrollTop, onScrollFrame, useMediaQuery, useMotionAllowed } from "./env";
import { smoothScrollTo } from "./smoothScroll";

const stickyTopOf = (el) => parseFloat(getComputedStyle(el).top) || 0;

export default function usePinnedSteps(trackRef, { stickyRef, count = 1, stepPx, property = "--ld-pin", query = DESKTOP } = {}) {
    const wide = useMediaQuery(query);
    const motion = useMotionAllowed();
    const pinned = wide && motion;
    const [active, setActive] = useState(0);
    const lastActive = useRef(0);

    useEffect(() => {
        const track = trackRef?.current;
        const sticky = stickyRef?.current;
        if (!pinned || !track || !sticky) {
            lastActive.current = 0;
            setActive(0);
            return undefined;
        }

        const measure = () => {
            const t = track.getBoundingClientRect();
            const scrolled = stickyTopOf(sticky) - t.top;
            const run = Math.max(t.height - sticky.getBoundingClientRect().height, 1);
            const p = stepPx
                ? Math.min(Math.max(scrolled / stepPx, 0), count)
                : clamp01(scrolled / run) * count;
            track.style.setProperty(property, p.toFixed(4));
            const next = Math.min(count - 1, Math.max(0, Math.floor(p)));
            if (next !== lastActive.current) {
                lastActive.current = next;
                setActive(next);
            }
        };

        measure();
        const off = onScrollFrame(measure);
        return () => {
            off();
            track.style.removeProperty(property);
        };
    }, [trackRef, stickyRef, count, stepPx, property, pinned]);

    const goTo = useCallback((i) => {
        const track = trackRef?.current;
        const sticky = stickyRef?.current;
        if (!track || !sticky) return;
        const t = track.getBoundingClientRect();
        const run = Math.max(t.height - sticky.getBoundingClientRect().height, 1);
        const into = stepPx ? stepPx * (i + 0.05) : (run * (i + 0.05)) / count;
        smoothScrollTo(getScrollTop() + t.top - stickyTopOf(sticky) + into);
    }, [trackRef, stickyRef, count, stepPx]);

    return { active, pinned, goTo };
}
